import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try emailing us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
        <header className="border-b border-slate-700 sticky top-0 z-50 shadow-lg bg-slate-900">
          <div className="container mx-auto px-6 py-4 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-300">
              <Building2 className="h-8 w-8" style={{ color: '#3b82f6' }} />
              <span className="text-2xl font-bold text-white">Top Prop</span>
            </a>
          </div>
        </header>

        <div className="container mx-auto px-6 py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center h-20 w-20 rounded-full mb-6" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
              <CheckCircle2 className="h-10 w-10" style={{ color: '#3b82f6' }} />
            </div>
            <h1 className="text-4xl font-bold mb-4" style={{ color: '#1E1E1E' }}>
              Message Received!
            </h1>
            <p className="text-lg mb-8" style={{ color: '#3A3A3A' }}>
              Thank you for contacting us. We'll get back to you within 24 hours.
            </p>
            <Button
              onClick={() => navigate("/")}
              className="text-white font-semibold transition-all duration-300"
              size="lg"
              style={{ 
                backgroundColor: '#3b82f6',
                borderRadius: '10px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
                e.currentTarget.style.color = '#000000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.color = '#FFFFFF';
              }}
            >
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <header className="border-b border-slate-700 sticky top-0 z-50 shadow-lg bg-slate-900">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-300">
            <Building2 className="h-8 w-8" style={{ color: '#3b82f6' }} />
            <span className="text-2xl font-bold text-white">Top Prop</span>
          </a>
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="transition-all duration-300"
            style={{ 
              borderColor: '#FFFFFF',
              color: '#FFFFFF',
              borderRadius: '10px'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; e.currentTarget.style.borderColor = '#3b82f6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = '#FFFFFF'; }}
          >
            Return to Home
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 py-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#1E1E1E' }}>
              Contact Us
            </h1>
            <p className="text-lg" style={{ color: '#3A3A3A' }}>
              Have questions or need support? We're here to help!
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <Card className="bg-white" style={{ border: '1px solid #F7F7F7', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}>
              <CardHeader>
                <CardTitle style={{ color: '#1E1E1E' }}>Send Us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" style={{ color: '#1E1E1E' }}>Name *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your name"
                      style={{ borderColor: '#9C9C9C' }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" style={{ color: '#1E1E1E' }}>Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      style={{ borderColor: '#9C9C9C' }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" style={{ color: '#1E1E1E' }}>Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      style={{ borderColor: '#9C9C9C' }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" style={{ color: '#1E1E1E' }}>Subject *</Label>
                    <Input
                      id="subject"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="How can we help?"
                      style={{ borderColor: '#9C9C9C' }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" style={{ color: '#1E1E1E' }}>Message *</Label>
                    <Textarea
                      id="message"
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us more about your inquiry..."
                      rows={5}
                      style={{ borderColor: '#9C9C9C' }}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full text-white font-semibold transition-all duration-300"
                    style={{ 
                      backgroundColor: '#3b82f6',
                      borderRadius: '10px'
                    }}
                    onMouseEnter={(e) => {
                      if (!submitting) {
                        e.currentTarget.style.background = 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
                        e.currentTarget.style.color = '#000000';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#3b82f6';
                      e.currentTarget.style.color = '#FFFFFF';
                    }}
                    disabled={submitting}
                  >
                    {submitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-6">
              <Card className="bg-white" style={{ border: '1px solid #F7F7F7', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                      <Mail className="h-6 w-6" style={{ color: '#3b82f6' }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-1" style={{ color: '#1E1E1E' }}>Email Us</h3>
                      <p className="mb-2" style={{ color: '#3A3A3A' }}>For general inquiries and support</p>
                      <a href="mailto:support@topprop.ca" className="transition-colors duration-300" style={{ color: '#3b82f6' }}>
                        support@topprop.ca
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white" style={{ border: '1px solid #F7F7F7', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(244, 162, 97, 0.15)' }}>
                      <MapPin className="h-6 w-6" style={{ color: '#F4A261' }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-1" style={{ color: '#1E1E1E' }}>Service Area</h3>
                      <p style={{ color: '#3A3A3A' }}>
                        Connecting property owners and managers across Canada
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white" style={{ border: '2px solid #3b82f6', borderRadius: '12px', backgroundColor: 'rgba(215, 38, 56, 0.05)' }}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#1E1E1E' }}>Response Time</h3>
                  <p className="text-sm" style={{ color: '#3A3A3A' }}>
                    We typically respond to all inquiries within 24 hours during business days.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-white py-12" style={{ backgroundColor: '#1E1E1E' }}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6" style={{ color: '#3b82f6' }} />
              <span className="text-xl font-bold">Top Prop</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <button
                onClick={() => navigate("/contact")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Contact Us
              </button>
              <span style={{ color: '#3A3A3A' }}>|</span>
              <button
                onClick={() => navigate("/company-signup")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Join as Property Manager
              </button>
            </div>
            <p className="text-sm" style={{ color: '#9C9C9C' }}>© 2025 Top Prop. All rights reserved.</p>
            
            {/* Admin Login Link */}
            <div className="mt-4">
              <button
                onClick={() => navigate("/admin-login")}
                className="text-xs underline transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#F4A261'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Admin Login
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}